<script lang="ts">
  import { onMount } from 'svelte';
  import Button from '../../../components/Button.svelte';
  import Input from '../../../components/Input.svelte';
  import Textarea from '../../../components/Textarea.svelte';
  import StatusBadge from '../../../components/StatusBadge.svelte';
  import { DonationRequestStatusEnum } from '../../../lib/api/admin/donation.service';
  import { push } from 'svelte-spa-router';
  import ConfirmApprovalModal from './ConfirmApprovalModal.svelte';
  import RejectRequestModal from './RejectRequestModal.svelte';
  import Notification from '../../../components/Notification.svelte';
  import employerLeaveRequestService from '../../../lib/api/employer/leaverequest';
  import { refreshEmployerPendingActions } from '../../../stores/employer-pending-actions.store';

  export let requestId = '';
  
  // Get the actual request ID from prop or URL
  function getRequestId(): string {
    if (requestId) return requestId;
    // For hash-based routing, query params are in the hash, not search
    const hash = window.location.hash;
    const queryString = hash.includes('?') ? hash.split('?')[1] : '';
    if (queryString) {
      const params = new URLSearchParams(queryString);
      return params.get('id') || '';
    }
    return '';
  }
  
  onMount(() => {
    // Extract the request ID from the URL hash (for hash-based routing)
    requestId = getRequestId();
    console.log("UpdateLeaveRequest requestId:", requestId);
  });
  
  let loading = true;
  let request: any = null;
  let error: string | null = null;
  let fallbackLogoSrc = '/placeholder-logo.png';

  // Form values
  let leaveHours = '';
  let employeeContribution = ''; // Dollar amount for employee's leave hours
  let employerContribution = ''; // Optional employer matching amount
  let totalDonationValue = '';
  let employerComment = '';

  let showConfirmModal = false;
  let showRejectModal = false;
  let rejectReason = '';
  let termsAccepted = false;

  // Notification state
  let notificationShow = false;
  let notificationType: 'success' | 'error' = 'success';
  let notificationMessage = '';
  
  function showNotification(message: string, type: 'success' | 'error' = 'success') {
    notificationMessage = message;
    notificationType = type;
    notificationShow = true;
    setTimeout(() => notificationShow = false, 4000);
  }

  const fetchRequestDetails = async (id: string) => {
    return await employerLeaveRequestService.getLeaveRequestDetail(id);
  };

  function handleImageError(e: Event) {
    const imgElement = e.target as HTMLImageElement;
    imgElement.src = fallbackLogoSrc;
  }

  onMount(async () => {
    // Always read from URL to ensure we have the latest ID
    const id = getRequestId();
    if (!id) {
      error = 'No request ID provided';
      loading = false;
      return;
    }
    
    // Update requestId if we got it from URL
    if (!requestId && id) {
      requestId = id;
    }
    
    try {
      request = await fetchRequestDetails(id);
      leaveHours = request.leave_hours?.toString() || '0';
      employeeContribution = '';
      employerContribution = '';
      totalDonationValue = '0.00';
    } catch (err: any) {
      error = 'Failed to load request details';
      showNotification('Failed to load request details', 'error');
      console.error(err);
    } finally {
      loading = false;
    }
  });

  function goBack() {
      push('/leave-requests');
  }

  function calculateTotal() {
    const employeeAmount = employeeContribution ? parseFloat(employeeContribution) : 0;
    const employerAmount = employerContribution ? parseFloat(employerContribution) : 0;
    const total = employeeAmount + employerAmount;
    totalDonationValue = total.toFixed(2);
  }

  function handleEmployeeContributionChange() {
    calculateTotal();
  }

  function handleEmployerContributionChange() {
    calculateTotal();
  }

  async function handleSaveAndApprove(hourlyRate: number, subtotalAmount: number, totalAmount: number) {
    
    if (!request) return;
    
    // Validate that employee contribution is provided
    if (!employeeContribution || parseFloat(employeeContribution) <= 0) {
      showNotification('Please enter the dollar value for the employee contribution', 'error');
      return;
    }
    
    try {
      loading = true;
      const leaveValueCents = Math.round(parseFloat(employeeContribution) * 100);
      const employerTopUpCents = employerContribution ? Math.round(parseFloat(employerContribution) * 100) : 0;
      
      await employerLeaveRequestService.approveLeaveRequest(request.uuid, {
        leave_value_cents: leaveValueCents,
        employer_top_up_cents: employerTopUpCents,
        employer_comment: employerComment || undefined,
        terms_accepted: true,
      });

      await refreshEmployerPendingActions();
      showNotification('Request approved successfully', 'success');
      // Navigate back to the request detail page
      const id = getRequestId();
      setTimeout(() => push(`/view-leave-request?id=${id}`), 1500);
    } catch (err: any) {
      const status = err?.response?.status;
      const message = err?.response?.data?.message || 'Failed to Approve Request';
      showNotification(message, 'error');
      if (status === 409) {
        setTimeout(() => push(`/view-leave-request?id=${getRequestId()}`), 2000);
      }
    } finally {
      loading = false;
    }
  }

  async function handleReject() {
    if (!request) return;
    
    try {
      loading = true;
      await employerLeaveRequestService.rejectLeaveRequest(request.uuid, rejectReason);
      showNotification('Request rejected successfully', 'success');
      setTimeout(() => push('#/leave-requests'), 1500);
    } catch (err: any) {
      console.error('Error rejecting request:', err);
      showNotification(err?.response?.data?.message || 'Failed to Reject Request', 'error');
    } finally {
      loading = false;
    }
  }

  function handleOpenConfirmModal() {
    if (!termsAccepted) {
      showNotification('Please accept the terms and conditions to approve this request.', 'error');
      return;
    }
    showConfirmModal = true;
  }
  function handleCloseConfirmModal() {
    showConfirmModal = false;
  }
  function handleConfirmApproval(hourlyRate: number, subtotalAmount: number, totalAmount: number) {
    handleSaveAndApprove(hourlyRate, subtotalAmount, totalAmount);
    showConfirmModal = false;
  }

  function handleOpenRejectModal() {
    showRejectModal = true;
  }
  function handleCloseRejectModal() {
    showRejectModal = false;
  }
  function handleSubmitRejection() {
    handleReject();
    showRejectModal = false;
  }
</script>

<div class="update-request-page">
  {#if loading}
    <div class="loading-container">
      <div class="loading-spinner"></div>
    </div>
  {:else if error}
    <div class="error-container">
      <p class="error-message">{error}</p>
      <Button variant="primary" on:click={() => location.reload()}>
        Try Again
      </Button>
    </div>
  {:else if request}
    {@const isPending = request.donation_request_status?.name?.toLowerCase() === 'pending'}
    {#if !isPending}
      <div class="error-container">
        <p class="error-message">This request is no longer pending. It may have been approved or rejected already.</p>
        <p class="error-detail">Status: {request.donation_request_status?.name || 'Unknown'}</p>
        <Button variant="primary" on:click={() => push(`/view-leave-request?id=${getRequestId()}`)}>
          View request
        </Button>
      </div>
    {:else}
    <div class="request-content">
      <!-- Employee Information Section -->
      <section class="info-section employee-info-section">
        <div class="employee-info-content">
          <div class="employee-header">
            <div class="avatar avatar-blue">A</div>
            <div class="employee-details">
              <h3 class="employee-name">{request.user?.full_name || 'Unknown Employee'}</h3>
            </div>
            <div class="status-badge-container">
              <StatusBadge status={request.donation_request_status?.name || 'pending'} />
            </div>
          </div>
        </div>
      </section>

      <!-- Donation Details Section -->
      <section class="info-section">
        <h2 class="section-title">Donation Details</h2>
        <div class="value-calculation">
          <div class="form-row">
            <div class="form-group">
              <Input 
                label="Requested Hours"
                type="number" 
                bind:value={leaveHours} 
                fullWidth={true}
                placeholder="0"
                disabled={true}
              />
              <p class="field-hint">Enter the dollar value below</p>
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <Input 
                label="Employee Contribution ($)"
                type="number" 
                step="0.01"
                bind:value={employeeContribution} 
                on:input={handleEmployeeContributionChange}
                fullWidth={true}
                placeholder="0.00"
                required={true}
              />
              <p class="field-hint">Enter the dollar value for {leaveHours} hours</p>
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <Input 
                label="Employer Matching (Optional) ($)"
                type="number" 
                step="0.01"
                bind:value={employerContribution} 
                on:input={handleEmployerContributionChange}
                fullWidth={true}
                placeholder="0.00"
              />
              <p class="field-hint">Optional matching contribution</p>
            </div>
          </div>
        </div>
      </section>

      <!-- Total Donation Value Section -->
      <section class="info-section">
        <h2 class="section-title">Total Donation Value</h2>
        <div class="total-donation-display">
            <Input 
              type="text" 
              disabled={true} 
              value={`$${totalDonationValue || '0.00'}`}
              fullWidth={true} 
              placeholder="$ 0.00"
              label="Total"
            />
        </div>
      </section>

      <!-- Comment Section -->
      <section class="info-section">
        <h2 class="section-title">Comment (Optional)</h2>
        <Textarea 
          placeholder="Add a message for the employee or for internal reference..." 
          bind:value={employerComment}
          fullWidth={true}
          rows={4}
        />
      </section>

      <!-- Take Action Section -->
      <section class="info-section">
        <h2 class="section-title">Take Action</h2>
        <div class="action-info">
          <p class="action-description">
            Ready to finalize this request?
          </p>
          <p class="action-detail">
            Approving will process the donation and notify the employee. Rejecting will require a reason.
          </p>
        </div>
        <div class="terms-group">
          <label class="terms-checkbox-label">
            <input
              type="checkbox"
              bind:checked={termsAccepted}
              aria-describedby="terms-accept-description"
              aria-required="true"
            />
            <span class="terms-text">I accept the <a href="https://www.leave2give.com.au/terms-conditions/" target="_blank" rel="noopener noreferrer">terms and conditions</a></span>
          </label>
          <span id="terms-accept-description" class="visually-hidden">Required to approve this request</span>
        </div>
        <div class="action-buttons">
          <Button variant="primary" on:click={handleOpenConfirmModal} disabled={loading || !termsAccepted}>Save & Approve</Button>
          <Button variant="secondary" on:click={handleOpenRejectModal} disabled={loading}>Reject Request</Button>
        </div>
      </section>
    </div>
    {/if}
    <ConfirmApprovalModal
      open={showConfirmModal}
      amount={totalDonationValue}
      employerContribution={employerContribution}
      employeeContribution={employeeContribution}
      leaveHours={leaveHours}
      onConfirm={handleConfirmApproval}
      onCancel={handleCloseConfirmModal}
    />
    <RejectRequestModal
      open={showRejectModal}
      bind:reason={rejectReason}
      onSubmit={handleSubmitRejection}
      onCancel={handleCloseRejectModal}
    />
  {/if}
</div>

<Notification 
  bind:show={notificationShow}
  type={notificationType}
  message={notificationMessage}
/>

<style>
  .update-request-page {
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 24px;
    background-color: #f7fafc;
    min-height: 100vh;
  }



  .loading-container, .error-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    text-align: center;
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    margin: 0 auto;
    max-width: 800px;
    width: 100%;
  }

  .loading-spinner {
    width: 2rem;
    height: 2rem;
    border: 3px solid #f3f4f6;
    border-top: 3px solid #3b82f6;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 1rem;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .error-message {
    color: #dc2626;
    font-size: 1.1rem;
    margin-bottom: 0.5rem;
  }

  .error-detail {
    color: #6b7280;
    font-size: 0.875rem;
    margin-bottom: 1rem;
  }

  .request-content {
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 0;
    max-width: 600px;
    width: 100%;
    margin-left: auto;
    margin-right: auto;
  }

  .info-section {
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    padding: 24px;
  }

  .employee-info-section {
    background: white;
  }

  .employee-info-content {
    width: 100%;
  }

  .employee-header {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .employee-details {
    flex: 1;
  }

  .employee-name {
    font-size: 1.125rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 4px 0;
  }



  .section-title {
    font-size: 1rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 24px 0;
  }

  .form-row {
    display: flex;
    gap: 24px;
    margin-bottom: 16px;
  }

  .form-group {
    flex: 1;
  }

  .avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    color: white;
    font-weight: 600;
    font-size: 1rem;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }

  .avatar-blue {
    background-color: #2563EB;
  }



  .terms-group {
    margin-bottom: 1rem;
  }

  .terms-checkbox-label {
    display: inline-flex;
    align-items: flex-start;
    gap: 0.5rem;
    cursor: pointer;
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
  }

  .terms-checkbox-label input[type="checkbox"] {
    margin-top: 0.2rem;
    width: 1rem;
    height: 1rem;
    accent-color: var(--color-primary, #ffb400);
  }

  .terms-text a {
    color: var(--color-link, #aaa);
    text-decoration: underline;
  }

  .terms-text a:hover {
    color: var(--color-primary-hover, #f0a800);
  }

  .visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  .action-buttons {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 16px;
    margin-top: 16px;
  }

  .action-buttons :global(button) {
    flex: 1;
    height: 52px;
    border-radius: 100px !important;
    padding: 10px;
    font-size: 1rem;
    font-weight: 500;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0;
    box-sizing: border-box;
  }

  .action-buttons :global(button.primary) {
    background-color: #f59e0b;
    color: #ffffff;
    border: none;
  }
  .action-buttons :global(button.primary:hover) {
    background-color: #d97706;
  }
  .action-buttons :global(button.secondary) {
    background-color: #e5e7eb;
    color: #374151;
    border: none;
  }
  .action-buttons :global(button.secondary:hover) {
    background-color: #d1d5db;
  }

  .action-info {
    background-color: #f8f9fa;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 16px;
  }

  .action-description {
    font-size: 1rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 8px 0;
  }

  .action-detail {
    font-size: 0.875rem;
    color: #6b7280;
    margin: 0;
  }

  .field-hint {
    font-size: 0.75rem;
    color: #6b7280;
    margin-top: 4px;
    margin-bottom: 0;
  }

  .total-donation-display {
    margin-top: 8px;
  }

  .total-donation-display :global(input) {
    font-size: 1.25rem;
    font-weight: 600;
    color: #059669;
  }

  /* Responsive design */
  @media (max-width: 768px) {
    .form-row {
      flex-direction: column;
      gap: 16px;
    }

    .employee-header {
      flex-direction: column;
      align-items: flex-start;
    }

    .status-badge-container {
      align-self: flex-start;
      margin-top: 8px;
    }

    .action-buttons {
      flex-direction: column;
      gap: 12px;
    }
  }
</style> 